import { EmberAnimationPlan, EmberEquipment, EmberJoints, emberEquipmentKey, planEmberAnimation } from "game-common/ember_sprite/generate"
import { Frame } from "game-common/ember_sprite/raster"
import { SCALE_MODES, Texture } from "pixi.js"

/** One Ember animation as textures, shared by every sprite drawing that humanoid doing that thing. */
export interface BakedEmberAnimation {
    textures: Texture[]
    frameMs: number
    loop: boolean
    strideCycle?: number
    /** For an attack on the move: where the gait's stride has got to when it ends (0..1). */
    gaitPhaseAfter?: number
    /** Per frame, where the head and fists are on it: effects that follow a body attach there. */
    joints: EmberJoints[]
    /** The arms hold a load in front: the sprite draws the carried item between the hands. */
    carrying?: boolean
    /** Per frame, the box round everything drawn on it (a portrait fits the figure, not the frame). */
    drawn: DrawnBounds[]
}

export interface DrawnBounds {
    x: number
    y: number
    width: number
    height: number
}

interface Waiter {
    callback: (baked: BakedEmberAnimation | null) => void
    cancelled?: () => boolean
}

interface Bake {
    key: string
    plan: EmberAnimationPlan
    textures: Texture[]
    drawn: DrawnBounds[]
    waiters: Waiter[]
}

const INTERVAL = 20

export const frameTexture = (frame: Frame): Texture =>
    Texture.fromBuffer(
        new Uint8Array(frame.pixels.buffer, frame.pixels.byteOffset, frame.pixels.byteLength),
        frame.width,
        frame.height,
        { scaleMode: SCALE_MODES.NEAREST },
    )

/** The box round a frame's opaque pixels (empty at the origin for a blank frame). */
export const drawnBounds = ({ width, height, pixels }: Frame): DrawnBounds => {
    let left = width
    let top = height
    let right = -1
    let bottom = -1
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (!pixels[(y * width + x) * 4 + 3]) continue
            if (x < left) left = x
            if (x > right) right = x
            if (y < top) top = y
            bottom = y
        }
    }
    return right < 0 ? { x: 0, y: 0, width: 0, height: 0 } : { x: left, y: top, width: right - left + 1, height: bottom - top + 1 }
}

/**
 * Renders Ember animations into textures, a frame at a time, on a per-tick time budget -- the same
 * shape as the LPC compositor's queue, for the same reason: a frame hitch on weak hardware is worse
 * than a body that finishes appearing a few ticks later. Results are cached for the session; there
 * are a handful of named humanoids, so the whole set is a few megabytes.
 */
export class EmberFrameBaker {
    private baked = new Map<string, BakedEmberAnimation>()
    private bakes = new Map<string, Bake>()
    private queue: Bake[] = []
    private timer: any
    budgetMs = 4
    now: () => number = () => (typeof performance !== "undefined" ? performance.now() : Date.now())

    // What the body holds and wears, and in what colours, is part of every frame, so of the key.
    private keyOf = (spriteName: string, animation: string, equipment: EmberEquipment) =>
        `${spriteName}/${animation}/${emberEquipmentKey(equipment)}`

    /** An animation already baked, now, or nothing (a screen rebuilt every keystroke can't wait a tick). */
    peek = (spriteName: string, animation: string, equipment: EmberEquipment = {}): BakedEmberAnimation | undefined =>
        this.baked.get(this.keyOf(spriteName, animation, equipment))

    request = (
        spriteName: string,
        animation: string,
        priority: boolean,
        callback: (baked: BakedEmberAnimation | null) => void,
        cancelled?: () => boolean,
        equipment: EmberEquipment = {},
    ): void => {
        const key = this.keyOf(spriteName, animation, equipment)
        const done = this.baked.get(key)
        if (done) {
            // Still asynchronous: the sprite's adopt path expects to run outside activateAnimation.
            setTimeout(() => !cancelled?.() && callback(done), 0)
            return
        }
        const inFlight = this.bakes.get(key)
        if (inFlight) {
            inFlight.waiters.push({ callback, cancelled })
            if (priority) this.promote(inFlight)
            return
        }
        const plan = planEmberAnimation(spriteName, animation, equipment)
        if (!plan) {
            setTimeout(() => !cancelled?.() && callback(null), 0)
            return
        }
        const bake: Bake = { key, plan, textures: [], drawn: [], waiters: [{ callback, cancelled }] }
        this.bakes.set(key, bake)
        if (priority) this.queue.unshift(bake)
        else this.queue.push(bake)
        if (!this.timer) this.timer = setInterval(this.process, INTERVAL)
    }

    private promote = (bake: Bake) => {
        const at = this.queue.indexOf(bake)
        if (at > 0) {
            this.queue.splice(at, 1)
            this.queue.unshift(bake)
        }
    }

    process = (): void => {
        const start = this.now()
        while (this.queue.length) {
            const bake = this.queue[0]
            // Nobody is waiting any more: leave it unbaked rather than spend the budget on it.
            if (bake.waiters.every(w => w.cancelled?.())) {
                this.queue.shift()
                this.bakes.delete(bake.key)
                continue
            }
            const frame = bake.plan.renderFrame(bake.textures.length)
            bake.textures.push(frameTexture(frame))
            bake.drawn.push(drawnBounds(frame))
            if (bake.textures.length >= bake.plan.frameCount) {
                this.queue.shift()
                this.finish(bake)
            }
            if (this.now() - start >= this.budgetMs) return
        }
        clearInterval(this.timer)
        this.timer = undefined
    }

    private finish = (bake: Bake) => {
        const { frameMs, loop, strideCycle, gaitPhaseAfter, carrying } = bake.plan
        const joints = bake.textures.map((_, i) => bake.plan.jointsAt(i))
        const baked: BakedEmberAnimation = { textures: bake.textures, frameMs, loop, strideCycle, gaitPhaseAfter, joints, carrying, drawn: bake.drawn }
        this.baked.set(bake.key, baked)
        this.bakes.delete(bake.key)
        bake.waiters.forEach(w => !w.cancelled?.() && w.callback(baked))
    }

    /** Drain everything now (tests, and previews that want every frame at once). */
    flush = (): void => {
        const budget = this.budgetMs
        this.budgetMs = Infinity
        this.process()
        this.budgetMs = budget
    }

    clear = (): void => {
        this.baked.forEach(b => b.textures.forEach(t => t.destroy(true)))
        this.baked.clear()
        this.bakes.clear()
        this.queue = []
        clearInterval(this.timer)
        this.timer = undefined
    }
}

export const emberFrameBaker = new EmberFrameBaker()
